import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { VolunteerFormState } from '../types';
import { FormPanel, FormSectionTitle, SectionTitle, ValidationText } from '../styles';
import { Text } from '@/components/UI/Typography/Text';
import { Box } from '@/layout/Box';
import { theme } from '@/theme/Theme';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isTablet: boolean;
}

const PersonalInfoCollectionStep = ({ errors, control, isTablet }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const paragraph19 = t('pbl.lawyerRegistration.personalInformationCollection.paragraph19', {
    defaultValue:
      '19. You may send data access or data correction requests or your request to withdraw any consent by contacting us: Address: Room 403, 4/F, West Wing, Justice Place, 11 Ice House Street, Central, Hong Kong Email address: dpo@ebram.org Attention: Data Protection Officer',
  });
  const [paragraph19BeforeEmail, paragraph19AfterEmail = ''] = paragraph19.split('dpo@ebram.org');

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>
        {t('pbl.lawyerRegistration.personalInformationCollection.section', { defaultValue: 'Section 8' })}
      </SectionTitle>
      <FormSectionTitle>
        {t('pbl.lawyerRegistration.steps.personalInformationCollection', {
          defaultValue: 'Personal Information Collection Statement',
        })}
      </FormSectionTitle>

      <Box marginBottom={30}>
        <Text fontSize={theme.fontSizes.base}>
          {t('pbl.lawyerRegistration.personalInformationCollection.paragraph17', {
            defaultValue:
              '17. The personal data collected in this form ("the data") will be used by the organiser of FreelegalLink, The Office of Thomas So Shiu-tsung, JP, Legislative Council Member and eBRAM (collectively "FreelegalLink Project Team") for the purpose of processing registration for enrolment to FreelegalLink\'s panel of lawyers.',
          })}
        </Text>
      </Box>
      <br />
      <Box marginBottom={30}>
        <Text fontSize={theme.fontSizes.base}>
          {t('pbl.lawyerRegistration.personalInformationCollection.paragraph18', {
            defaultValue:
              '18. The data may be provided to such persons within the FreelegalLink Project Team whose proper business it is to have access to and assist in the processing of this registration and matters relating to the provision and evaluation of pro bono legal service under the Scheme. The data may also be provided to members of the public who seek to obtain pro bono legal advice and to such other persons including those who help members of the public in the process of seeking pro bono legal advice under the Scheme and those who may help the FreelegalLink Project Team in attaining the purposes mentioned above.',
          })}
        </Text>
      </Box>
      <br />
      <Box marginBottom={30}>
        <Text fontSize={theme.fontSizes.base}>
          {paragraph19BeforeEmail}
          <a href="mailto:dpo@ebram.org">
            <span style={{ color: theme.colors.primary400, textDecoration: 'underline' }}>dpo@ebram.org</span>
          </a>
          {paragraph19AfterEmail}
        </Text>
      </Box>

      <Box padding={'10px 0 0 0'}>
        <FormGroup>
          <Controller
            control={control}
            name="pics_agreed"
            rules={{
              validate: (value) =>
                value ||
                t('pbl.lawyerRegistration.personalInformationCollection.errors.acknowledgementRequired', {
                  defaultValue: 'You must acknowledge this statement to continue.',
                }),
            }}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />
                }
                label={t('pbl.lawyerRegistration.personalInformationCollection.agreement', {
                  defaultValue: 'I acknowledge and agree to the above personal data collection statement.',
                })}
              />
            )}
          />
        </FormGroup>
        {errors.pics_agreed?.message && <ValidationText>{errors.pics_agreed.message}</ValidationText>}
      </Box>
    </FormPanel>
  );
};

export default PersonalInfoCollectionStep;
